/**
 * Role Detail Drawer
 *
 * Slide-out panel showing full role details: classification, F&O linkage, change history.
 * Extracted from RoleMasterPage for modularity.
 */

import React from 'react';
import { Clock, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetDescription, SheetFooter,
  SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { RoleMasterDetail, RoleMasterStatus } from '@/services/api/roleMasterService';
import { RoleProfileCard } from '@/components/role-profiles/RoleProfileCard';

const STATUS_CONFIG: Record<RoleMasterStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  deprecated: { label: 'Deprecated', color: 'bg-gray-100 text-gray-500' },
  draft: { label: 'Draft', color: 'bg-amber-100 text-amber-700' },
};

interface RoleDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: RoleMasterDetail | null;
  onEdit: () => void;
  onDeprecate: () => void;
}

const RoleDetailDrawer: React.FC<RoleDetailDrawerProps> = ({
  open,
  onOpenChange,
  entry,
  onEdit,
  onDeprecate: _onDeprecate,
}) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        {entry && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <span className="font-mono text-heritage-blue">{entry.role_code}</span>
                <Badge className={cn('text-xs', STATUS_CONFIG[entry.status]?.color)}>
                  {STATUS_CONFIG[entry.status]?.label}
                </Badge>
              </SheetTitle>
              <SheetDescription>{entry.title}</SheetDescription>
            </SheetHeader>
            <div className="space-y-4 mt-4">
              {/* Core */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Job Family:</span>{' '}
                  <span className="font-medium">{entry.job_family || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Function:</span>{' '}
                  <span className="font-medium">{entry.functional_area || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Grade Level:</span>{' '}
                  <span className="font-medium">{entry.grade_level || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Position Level:</span>{' '}
                  <span className="font-medium">{entry.position_level || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Department:</span>{' '}
                  <span className="font-medium">{entry.department || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Team 1:</span>{' '}
                  <span className="font-medium">{entry.team_1 || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Team 2:</span>{' '}
                  <span className="font-medium">{entry.team_2 || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Team 3:</span>{' '}
                  <span className="font-medium">{entry.team_3 || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Academic:</span>{' '}
                  <span className="font-medium">{entry.is_academic ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Source:</span>{' '}
                  <span className="font-medium">{entry.source}</span>
                </div>
                <div>
                  <span className="text-gray-500">Linked Positions:</span>{' '}
                  <span className="font-medium">{entry.linked_positions_count}</span>
                </div>
                <div>
                  <span className="text-gray-500">Sage Title Code:</span>{' '}
                  <span className="font-medium">{entry.sage_title_code || '—'}</span>
                </div>
              </div>

              {/* Remuneration */}
              {(entry.remco_grade || entry.remchannel_match_code || entry.remchannel_grade_match) && (
                <>
                  <hr className="border-gray-200" />
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Remuneration</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Remco Grade:</span>{' '}
                      <span className="font-medium">{entry.remco_grade || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Remchannel Match Code:</span>{' '}
                      <span className="font-medium">{entry.remchannel_match_code || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Remchannel Grade Match:</span>{' '}
                      <span className="font-medium">{entry.remchannel_grade_match || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Remchannel Title Match:</span>{' '}
                      <span className="font-medium">{entry.remchannel_title_match || '—'}</span>
                    </div>
                  </div>
                </>
              )}

              {/* Finance */}
              {(entry.finance_department_name || entry.finance_department_code || entry.role_allocation_type) && (
                <>
                  <hr className="border-gray-200" />
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Finance</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Finance Dept Name:</span>{' '}
                      <span className="font-medium">{entry.finance_department_name || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Finance Dept Code:</span>{' '}
                      <span className="font-medium">{entry.finance_department_code || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Role Allocation:</span>{' '}
                      <span className="font-medium">{entry.role_allocation_type || '—'}</span>
                    </div>
                  </div>
                </>
              )}

              {/* Attributes */}
              {(entry.remote_work || entry.customer_facing !== undefined || entry.revenue_generating || entry.is_critical_role !== undefined || entry.budget_status || entry.occupational_level) && (
                <>
                  <hr className="border-gray-200" />
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Attributes</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {entry.remote_work && (
                      <div>
                        <span className="text-gray-500">Remote Work:</span>{' '}
                        <span className="font-medium">{entry.remote_work}</span>
                      </div>
                    )}
                    {entry.customer_facing !== undefined && entry.customer_facing !== null && (
                      <div>
                        <span className="text-gray-500">Customer Facing:</span>{' '}
                        <span className="font-medium">{entry.customer_facing ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {entry.revenue_generating && (
                      <div>
                        <span className="text-gray-500">Revenue Generating:</span>{' '}
                        <span className="font-medium">{entry.revenue_generating}</span>
                      </div>
                    )}
                    {entry.is_critical_role !== undefined && entry.is_critical_role !== null && (
                      <div>
                        <span className="text-gray-500">Critical Role:</span>{' '}
                        <span className="font-medium">{entry.is_critical_role ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {entry.is_critical_cohort !== undefined && entry.is_critical_cohort !== null && (
                      <div title="Not critical across the organisation, but a minimum number of staff is needed in this role, at each campus for instance">
                        <span className="text-gray-500">Critical Cohort:</span>{' '}
                        <span className="font-medium">{entry.is_critical_cohort ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {entry.budget_status && (
                      <div>
                        <span className="text-gray-500">Budget Status:</span>{' '}
                        <span className="font-medium">{entry.budget_status}</span>
                      </div>
                    )}
                    {entry.occupational_level && (
                      <div>
                        <span className="text-gray-500">Occupational Level:</span>{' '}
                        <span className="font-medium">{entry.occupational_level}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Classification - Role Clusters */}
              {(entry.role_cluster_skills || entry.role_cluster_grade_1 || entry.role_cluster_grade_2) && (
                <>
                  <hr className="border-gray-200" />
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Classification</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {entry.role_cluster_skills && (
                      <div>
                        <span className="text-gray-500">Role Cluster Skills:</span>{' '}
                        <span className="font-medium">{entry.role_cluster_skills}</span>
                      </div>
                    )}
                    {entry.role_cluster_grade_1 && (
                      <div>
                        <span className="text-gray-500">Grade-based 1:</span>{' '}
                        <span className="font-medium">{entry.role_cluster_grade_1}</span>
                      </div>
                    )}
                    {entry.role_cluster_grade_2 && (
                      <div>
                        <span className="text-gray-500">Grade-based 2:</span>{' '}
                        <span className="font-medium">{entry.role_cluster_grade_2}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {entry.notes && (
                <div className="text-sm">
                  <span className="text-gray-500">Notes:</span>
                  <p className="mt-1 text-gray-700">{entry.notes}</p>
                </div>
              )}

              {/* Dates */}
              {(entry.effective_date || entry.expiration_date) && (
                <>
                  <hr className="border-gray-200" />
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Dates</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Effective Date:</span>{' '}
                      <span className="font-medium">{entry.effective_date ? new Date(entry.effective_date).toLocaleDateString() : '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Expiration Date:</span>{' '}
                      <span className="font-medium">{entry.expiration_date ? new Date(entry.expiration_date).toLocaleDateString() : '—'}</span>
                    </div>
                  </div>
                </>
              )}

              {/* F&O Linkage */}
              {(entry.fo_title_id || entry.fo_job_id) && (
                <div className="border rounded p-3 bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">F&O Linkage</h4>
                  <div className="text-sm space-y-1">
                    {entry.fo_title_id && <div>Title ID: <code>{entry.fo_title_id}</code></div>}
                    {entry.fo_job_id && <div>Job ID: <code>{entry.fo_job_id}</code></div>}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <hr className="border-gray-200" />
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-400">
                <div>Created: {new Date(entry.created_at).toLocaleString()}</div>
                {entry.updated_at && <div>Updated: {new Date(entry.updated_at).toLocaleString()}</div>}
              </div>

              {/* Role profile (hides itself for viewers without Role Master access) */}
              <RoleProfileCard roleId={entry.id} />

              {/* Change History */}
              {entry.change_logs.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Change History</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {entry.change_logs.map((log) => (
                      <div key={log.id} className="flex items-start gap-2 text-sm border-l-2 border-gray-200 pl-3">
                        <Clock className="h-3 w-3 text-gray-400 mt-1 flex-shrink-0" />
                        <div>
                          <span className="font-medium">{log.change_type}</span>
                          {log.field_changed && <span className="text-gray-500"> ({log.field_changed})</span>}
                          {log.previous_value && log.new_value && (
                            <span className="text-gray-500">
                              : {log.previous_value} → {log.new_value}
                            </span>
                          )}
                          <div className="text-xs text-gray-400">
                            {log.changed_by_name || 'System'} · {new Date(log.created_at).toLocaleDateString()}
                            {log.change_reason && <span> · {log.change_reason}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <SheetFooter className="mt-6">
              <Button variant="outline" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-1" /> Edit
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default RoleDetailDrawer;
